/**
 * Build Venturu OS URLs with context segment.
 * Personal: ~{handle}, Organization: {orgSlug}, Staff: _platform
 */
export type OsContext =
  | { type: "personal"; handle: string }
  | { type: "org"; slug: string }
  | { type: "team"; orgSlug: string; teamSlug: string }
  | { type: "member"; orgSlug: string; memberHandle: string }
  | { type: "staff" };

export const STAFF_PLATFORM_CONTEXT_PARAM = "_platform";

export function formatContextSegment(ctx: OsContext): string {
  switch (ctx.type) {
    case "personal":
      return `~${ctx.handle}`;
    case "org":
      return ctx.slug;
    case "team":
      return ctx.orgSlug;
    case "member":
      return ctx.orgSlug;
    case "staff":
      return STAFF_PLATFORM_CONTEXT_PARAM;
  }
}

export function osHref(ctx: OsContext, path = "/dashboard"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;

  switch (ctx.type) {
    case "personal":
    case "org":
    case "staff":
      return `/os/${formatContextSegment(ctx)}${normalized}`;
    case "team":
      return `/os/${ctx.orgSlug}/teams/${ctx.teamSlug}${normalized}`;
    case "member":
      return `/os/${ctx.orgSlug}/members/${ctx.memberHandle}${normalized}`;
  }
}

export type OsWorkspaceViewTypeFromPath = "broker" | "team" | "org" | "staff";

/** Workspace view implied by an OS pathname, or null when the path is not under `/os`. */
export function osWorkspaceViewTypeFromPathname(
  pathname: string,
): OsWorkspaceViewTypeFromPath | null {
  const parsed = parseContextFromPathname(pathname);
  if (!parsed.contextParam) return null;
  if (parsed.contextParam === STAFF_PLATFORM_CONTEXT_PARAM) return "staff";
  if (parsed.contextParam.startsWith("~")) return "broker";
  if (parsed.memberHandle) return "broker";
  if (parsed.teamSlug) return "team";
  return "org";
}

export function parseContextFromPathname(pathname: string): {
  contextParam: string | null;
  teamSlug: string | null;
  memberHandle: string | null;
  subPath: string;
} {
  const match = pathname.match(/^\/os\/([^/]+)(\/.*)?$/);
  if (!match) {
    return { contextParam: null, teamSlug: null, memberHandle: null, subPath: "/dashboard" };
  }

  const contextParam = match[1] ?? null;
  const rest = match[2] ?? "";

  const teamMatch = rest.match(/^\/teams\/([^/]+)(\/.*)?$/);
  if (teamMatch) {
    return {
      contextParam,
      teamSlug: teamMatch[1] ?? null,
      memberHandle: null,
      subPath: teamMatch[2] || "/dashboard",
    };
  }

  const memberMatch = rest.match(/^\/members\/([^/]+)(\/.*)?$/);
  if (memberMatch) {
    return {
      contextParam,
      teamSlug: null,
      memberHandle: memberMatch[1] ?? null,
      subPath: memberMatch[2] || "/dashboard",
    };
  }

  return {
    contextParam,
    teamSlug: null,
    memberHandle: null,
    subPath: rest || "/dashboard",
  };
}

/** Conversation id from an OS subpath like `/ai` or `/ai/{id}`. */
export function parseAiConversationIdFromSubPath(subPath: string): string | undefined {
  const match = subPath.match(/^\/ai\/([^/]+)/);
  return match?.[1];
}

export function parseAiConversationIdFromPathname(pathname: string): string | undefined {
  return parseAiConversationIdFromSubPath(parseContextFromPathname(pathname).subPath);
}

export function isPersonalContextParam(param: string): boolean {
  return param.startsWith("~");
}

export type OsRouteOpts = {
  teamSlug?: string;
  memberHandle?: string;
};

/** Build a context-scoped OS path from route params (client or server). */
export function buildOsHrefFromRoute(
  contextParam: string,
  path: string,
  opts?: OsRouteOpts,
): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;

  if (contextParam === STAFF_PLATFORM_CONTEXT_PARAM) {
    return osHref({ type: "staff" }, normalized);
  }

  if (opts?.memberHandle && !contextParam.startsWith("~")) {
    return osHref(
      { type: "member", orgSlug: contextParam, memberHandle: opts.memberHandle },
      normalized,
    );
  }
  if (opts?.teamSlug && !contextParam.startsWith("~")) {
    return osHref(
      { type: "team", orgSlug: contextParam, teamSlug: opts.teamSlug },
      normalized,
    );
  }
  if (contextParam.startsWith("~")) {
    return osHref({ type: "personal", handle: contextParam.slice(1) }, normalized);
  }
  return osHref({ type: "org", slug: contextParam }, normalized);
}
